import {
  AfterViewInit,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ConfirmationLockService } from '../../core/services/confirmation-lock.service';
import {
  restoreFocusTo,
  wrapTabFocus,
} from '../../core/utils/focus-trap.util';
import { ModalStackService } from '../../core/services/modal-stack.service';
import { OverlayCoordinatorService } from '../../core/services/overlay-coordinator.service';

/**
 * Modal dialog from design system (Pencil modal-dialog).
 * Header: title + close; body: projected content; footer: optional actions.
 */
@Component({
  selector: 'app-modal',
  standalone: true,
  imports: [CommonModule],
  template: `
    @if (!blocked) {
    <div class="modal-overlay" (click)="onOverlayClick()">
      <div
        #dialog
        class="modal-dialog"
        [class.modal-dialog--sm]="size === 'sm'"
        [class.modal-dialog--md]="size === 'md'"
        [class.modal-dialog--lg]="size === 'lg'"
        [class.modal-dialog--xl]="size === 'xl'"
        (click)="$event.stopPropagation()"
        role="dialog"
        aria-modal="true"
        tabindex="-1"
        [attr.aria-labelledby]="titleId"
      >
        <div class="modal-header">
          <!-- BE-605: titleTooltip показывает полное значение для обрезанных заголовков -->
          <h2 [id]="titleId" class="modal-title" [attr.title]="titleTooltip || null">{{ title }}</h2>
          <button
            type="button"
            class="modal-close"
            (click)="close.emit()"
            [attr.aria-label]="closeLabel"
          >
            <span aria-hidden="true">×</span>
          </button>
        </div>
        <div class="modal-body">
          <ng-content></ng-content>
        </div>
        @if (showFooter) {
          <div class="modal-footer">
            <ng-content select="[footer]"></ng-content>
          </div>
        }
      </div>
    </div>
    }
  `,
  styles: [
    `
      .modal-overlay {
        position: fixed;
        inset: 0;
        background: var(--bg-overlay);
        display: flex;
        align-items: flex-start;
        justify-content: center;
        padding: 60px 24px 24px;
        z-index: 1100;
        overflow-y: auto;
        /* BE-1022: не прокидывать скролл на страницу под оверлеем. */
        overscroll-behavior: contain;
      }

      .modal-dialog {
        background: var(--bg-card);
        border-radius: var(--radius-xl);
        border: 1px solid var(--border-default);
        box-shadow: var(--shadow-modal);
        width: 100%;
        max-width: 480px;
        /* BE-1022: считаем по РЕАЛЬНО видимой высоте вьюпорта. На мобиле 100vh
           включает область под панелями браузера — окно вылезало за фолд, и низ
           контента доставался только прокруткой всего оверлея. dvh отражает
           динамический вьюпорт; vh оставлен фолбэком для старых браузеров. */
        max-height: calc(100vh - 84px);
        max-height: calc(100dvh - 84px);
        display: flex;
        flex-direction: column;
        flex-shrink: 0;
      }

      .modal-dialog--sm {
        max-width: 400px;
      }

      .modal-dialog--md {
        max-width: 480px;
      }

      .modal-dialog--lg {
        max-width: 560px;
      }

      .modal-dialog--xl {
        max-width: 880px;
      }

      .modal-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        height: 56px;
        padding: 0 24px;
        border-bottom: 1px solid var(--border-light);
        flex-shrink: 0;
      }

      .modal-title {
        margin: 0;
        font-size: 16px;
        font-weight: 600;
        color: var(--text-primary);
        font-family: var(--font-main);
      }

      .modal-close {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 32px;
        height: 32px;
        border: none;
        background: transparent;
        color: var(--text-muted);
        cursor: pointer;
        border-radius: var(--radius-sm);
        font-size: 20px;
        line-height: 1;
        transition: color 0.2s ease, background 0.2s ease;
      }

      .modal-close:hover {
        color: var(--text-primary);
        background: var(--bg-hover);
      }

      .modal-body {
        padding: 24px;
        overflow-y: auto;
        flex: 1;
        min-height: 0;
        /* BE-1022: единственный скролл-контейнер контента; не прокидывать скролл
           на оверлей/страницу, чтобы прокрутка доходила до конца без «залипания». */
        overscroll-behavior: contain;
        -webkit-overflow-scrolling: touch;
      }

      .modal-footer {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: 12px;
        padding: 16px 24px;
        border-top: 1px solid var(--border-light);
        flex-shrink: 0;
      }

      /* Ряд кнопок футера один на все окна: страницы передают его блоком [footer]
         со своими классами, и у каждой была своя раскладка — на телефоне кнопки
         выходили лесенкой разной ширины, а в «Пригласить сотрудника» вылезали за край. */
      .modal-footer ::ng-deep > [footer] {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        flex-wrap: wrap;
        gap: 12px;
        width: 100%;
        margin: 0;
      }

      @media (max-width: 768px) {
        .modal-overlay {
          padding: 24px 16px 16px;
        }

        .modal-dialog {
          max-height: calc(100vh - 40px);
          max-height: calc(100dvh - 40px);
        }

        .modal-header {
          padding: 0 16px;
        }

        .modal-body {
          padding: 16px;
        }

        .modal-footer {
          padding: 12px 16px;
          padding-bottom: calc(12px + env(safe-area-inset-bottom, 0px));
        }

        /* Две кнопки — в одну строку поровну; три и больше в строку не влезают —
           столбиком во всю ширину, основное действие (последнее в разметке) сверху. */
        .modal-footer ::ng-deep > [footer] {
          gap: 8px;
        }

        /* Поровну, но не уже подписи: если две кнопки вместе не влезают, вторая
           переносится на свою строку во всю ширину, а не обрезается. */
        .modal-footer ::ng-deep > [footer] > * {
          flex: 1 1 0;
          min-width: max-content;
          max-width: 100%;
        }

        .modal-footer ::ng-deep > [footer] > app-button {
          display: block;
        }

        .modal-footer ::ng-deep > [footer]:has(> :nth-child(3)) {
          flex-direction: column-reverse;
          align-items: stretch;
        }

        .modal-footer ::ng-deep > [footer]:has(> :nth-child(3)) > * {
          flex: 0 0 auto;
          width: 100%;
        }
      }
    `,
  ],
})
export class ModalComponent implements OnInit, AfterViewInit {
  @Input() title = '';
  /** BE-605: полное значение для тултипа заголовка (например, несокращённый UUID). */
  @Input() titleTooltip = '';
  @Input() size: 'sm' | 'md' | 'lg' | 'xl' = 'md';
  @Input() closeLabel = 'Закрыть';
  @Input() closeOnOverlay = true;
  @Input() showFooter = false;
  /**
   * BE-1237: чем окно является. `confirm` занимает слой подтверждения и проверяет его:
   * подтверждение поверх подтверждения не поднимаем. Дефолт `content` слой не трогает —
   * подтверждение поверх контентного окна остаётся штатным сценарием.
   *
   * Читается ОДИН раз, в ngOnInit: `[layer]` с меняющимся значением молча не сработает.
   *
   * BE-1238, критерий отбора: слой `confirm` ставится окну, которое спрашивает «точно?»
   * перед НЕОБРАТИМЫМ действием — прекращение акции, подтверждение оплаты, отказ от
   * Заявки, возврат акции в черновик. Наличие поля ввода внутри окна признаком не
   * является: «Подтвердить оплату» просит ID платежа и всё равно подтверждение.
   * НЕ ставится трём видам окон: формам и справкам; окнам-уведомлениям с единственной
   * кнопкой «Понятно» (там нет выбора между исходами); и — это важнее всего — любому
   * окну, ПОВЕРХ которого штатно поднимается подтверждение, например «Заявка на акцию»
   * с её же окном отказа. Пометить такое окно значит сломать сценарий насовсем:
   * дочернее подтверждение перестанет открываться, и без единой ошибки.
   *
   * КОНТРАКТ для владельца окна со слоем `confirm`: на `(close)` обязательно гасить свой
   * флаг показа. Отбитое окно отдаёт `(close)` сразу, и если флаг останется поднятым,
   * окно не появится уже никогда — решение об отбое одноразовое, а компонент остаётся
   * смонтированным, поэтому повторный клик не создаст новый инстанс и не даст даже тоста.
   */
  @Input() layer: 'content' | 'confirm' = 'content';

  @Output() close = new EventEmitter<void>();

  titleId = 'modal-title-' + Math.random().toString(36).slice(2, 9);

  @ViewChild('dialog') private dialogRef?: ElementRef<HTMLElement>;

  private readonly destroyRef = inject(DestroyRef);
  private readonly overlayCoordinator = inject(OverlayCoordinatorService);
  private readonly modalStack = inject(ModalStackService);
  private readonly confirmationLock = inject(ConfirmationLockService);

  /**
   * BE-1237: слой занят другим подтверждением — окно не показываем вовсе. Обычное поле,
   * а не сигнал, намеренно: значение выставляется один раз в ngOnInit, до первого рендера
   * собственной вью, и меняться не должно. Пересчёт «а не освободился ли слой» поднял бы
   * окно позже, когда пользователь этого вопроса уже не ждёт.
   */
  protected blocked = false;

  /**
   * BE-1239: элемент, которым окно открыли, — чтобы вернуть на него фокус. Снимается в
   * ngOnInit: к ngAfterViewInit фокус уже уехал в окно, а раньше @Input `layer` ещё не
   * проставлен, и отбитое окно успело бы запомнить чужого открывателя. Открывателя может
   * не быть вовсе — окно открывается и из ngOnInit по query-параметру, и из колбэка
   * ответа сервера.
   */
  private opener: HTMLElement | null = null;

  /** До постановки в стек окно не верхнее — отбитое не встаёт в стек никогда. */
  private isTopModal: () => boolean = () => false;

  constructor() {
    this.destroyRef.onDestroy(() => this.restoreFocus());
  }

  ngOnInit(): void {
    // Решение о слое принимаем здесь, а не в инициализаторе поля: к моменту создания
    // класса Angular ещё не проставил значения входов.
    if (this.layer === 'confirm' && this.confirmationLock.locked) {
      this.blocked = true;
      this.confirmationLock.noticeBlocked();
      // Ни открывателя, ни записи в стеке: иначе невидимое окно забрало бы у видимого
      // подтверждения Esc, обход по Tab и возврат фокуса.
      //
      // Закрытие отдаём наверх — для владельца это ровно «пользователь не подтвердил».
      // Но НЕ внутри прохода change detection: владелец, который гейтит окно обычным
      // полем, а не сигналом, получил бы ExpressionChangedAfterItHasBeenCheckedError.
      // Микрозадача выполняется после прохода вместе с его dev-проверкой.
      queueMicrotask(() => this.close.emit());
      return;
    }

    if (this.layer === 'confirm') {
      this.confirmationLock.acquire(this.destroyRef);
      this.confirmationLock.resetNotice();
    }

    this.opener = document.activeElement as HTMLElement | null;
    this.isTopModal = this.modalStack.push(this.destroyRef);
  }

  ngAfterViewInit(): void {
    // Фокус на контейнер, а не на первый элемент: первым в разметке идёт крестик, и
    // Enter сразу после открытия закрывал бы окно.
    this.dialogRef?.nativeElement.focus();
  }

  onOverlayClick(): void {
    if (this.closeOnOverlay) this.close.emit();
  }

  // Слушаем именно нужные сочетания, а не keydown целиком: приложение zoneless, и общий
  // хост-слушатель планировал бы полный проход change detection на КАЖДУЮ нажатую клавишу,
  // пока открыто хоть одно окно. Ключевые имена событий Angular отсеивает до обёртки CD.
  @HostListener('document:keydown.escape', ['$event'])
  onEscape(event: Event): void {
    // Клавиатуру обрабатывает только верхнее окно: остальные ждут своей очереди.
    if (!this.isTopModal()) {
      return;
    }
    // Раскрытый внутри окна список, календарь или подсказка забирает Esc себе: иначе
    // первый же Esc в форме выбросил бы вместе с ним и всё набранное.
    if (this.overlayCoordinator.closeActive() || event.defaultPrevented) {
      return;
    }
    // Esc = крестик: у обёрток подтверждения закрытие означает отмену.
    this.close.emit();
  }

  @HostListener('document:keydown.tab', ['$event'])
  @HostListener('document:keydown.shift.tab', ['$event'])
  onTab(event: Event): void {
    if (!this.isTopModal()) {
      return;
    }
    const dialog = this.dialogRef?.nativeElement;
    if (!dialog) {
      return;
    }
    // BE-1293: механика удержания вынесена в core/utils/focus-trap.util — её же
    // использует выезжающая панель, чтобы не копировать поведение.
    wrapTabFocus(dialog, event);
  }

  private restoreFocus(): void {
    restoreFocusTo(this.opener, this.dialogRef?.nativeElement);
  }
}
